/**
 * IDA-UPI: bank instrument attestations, mandates and payments
 * (white paper §6, §7). Amounts are integer paise.
 */
import { apiFetch } from './client';
import { encodePathSegment } from '../lib/url';
import { proofValueFor, signingMethod, type IssuerSigner, type Prepared } from '../lib/credentialSigning';

export type InstrumentKind = 'upi-circle-partial' | 'upi-circle-full' | 'upi-reserve-pay' | 'npci-framework';

export const INSTRUMENT_LABELS: Record<string, string> = {
  'upi-circle-partial': 'UPI Circle (customer approves each payment)',
  'upi-circle-full': 'UPI Circle (full delegation)',
  'upi-reserve-pay': 'UPI Reserve Pay block',
  'npci-framework': 'NPCI agent framework',
};

export interface Instrument {
  kind: string;
  ref: string;
  attestation: string;
}

export interface Attestation {
  id: string;
  bank: string;
  type: string;
  kind: string;
  instrumentRef: string;
  agent?: string;
  customerDidHash: string;
  limits: Record<string, unknown>;
  validUntil: string;
  usable: boolean;
  problem?: string;
}

export interface SponsorBanks {
  banks: string[];
  paymentsEnabled: boolean;
  mockBank?: string;
}

export interface ConsentRecord {
  id: string;
  mandateId: string;
  purpose: string;
  termsHash: string;
  proposalId?: string;
  supersedes?: string;
  givenAt: string;
  withdrawnAt?: string;
  withdrawnReason?: string;
}

export type MandateStatus = 'active' | 'revoked' | 'superseded' | 'expired' | 'bank-suspended';

export interface Mandate {
  id: string;
  customerDid: string;
  agentDid: string;
  scope: string[];
  constraints: Record<string, unknown>;
  instrument: Instrument;
  validFrom: string;
  validUntil: string;
  status: MandateStatus;
  reason?: string;
  supersededBy?: string;
  consent?: ConsentRecord;
  spend?: { daySpentPaise: number; periodSpentPaise: number; periodStart?: string };
}

export type ProposalStatus = 'proposed' | 'consented' | 'declined' | 'withdrawn' | 'expired';

export interface Proposal {
  id: string;
  customerDid: string;
  agentDid: string;
  purpose: string;
  constraints: Record<string, unknown>;
  validUntil: string;
  instrument: Instrument;
  status: ProposalStatus;
  mandateId?: string;
  createdAt: string;
  expiresAt: string;
}

export type PaymentStatus = 'SUBMITTED' | 'PENDING_HUMAN' | 'AUTHORISED' | 'DECLINED' | 'EXPIRED';

export interface PaymentEvent {
  from?: PaymentStatus;
  to: PaymentStatus;
  source: 'submit' | 'bank' | 'reconcile';
  at: string;
}

export interface Payment {
  id: string;
  rootMandateId: string;
  customerDid: string;
  agentDid: string;
  merchantDid: string;
  merchantOrderId: string;
  payeeVpa: string;
  amountPaise: number;
  instrumentKind: string;
  bankDid: string;
  decisionId?: string;
  humanApproval: boolean;
  status: PaymentStatus;
  rrn?: string;
  reasonCode?: string;
  createdAt: string;
  updatedAt: string;
  events?: PaymentEvent[];
}

/** What a merchant gets back from POST /upi/payments. */
export interface PaymentResult {
  paymentId?: string;
  status: PaymentStatus;
  rrn?: string;
  reasonCode?: string;
  decision: string;
  decisionId?: string;
}

export interface DecisionReplay {
  signatureValid: boolean;
  matches: boolean;
  recorded?: unknown;
  replayed?: unknown;
}

/** ₹ from paise, e.g. 84900 → ₹849.00. */
export function rupees(paise: unknown): string {
  const n = typeof paise === 'number' ? paise : Number(paise);
  if (!Number.isFinite(n)) return '—';
  return '₹' + (n / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Paise from a rupee amount typed by a person; null if it is not whole paise. */
export function toPaise(rupeesText: string): number | null {
  const t = rupeesText.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole, frac = ''] = t.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

const q = (params: Record<string, string | undefined>) =>
  new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString();

export const upi = {
  sponsorBanks: () => apiFetch<SponsorBanks>('/upi/sponsor-banks'),
  attestations: (agent?: string, customer?: string) => apiFetch<Attestation[]>(`/upi/attestations?${q({ agent, customer })}`),
  mockAttest: (body: Record<string, unknown>) => apiFetch<{ id: string }>('/upi/mock-bank/attestations', { method: 'POST', body: JSON.stringify(body) }),
  mockRemove: (id: string, customerDid: string) =>
    apiFetch(`/upi/mock-bank/attestations/${encodePathSegment(id)}/remove`, { method: 'POST', body: JSON.stringify({ customerDid }) }),

  mandates: (who: { customer?: string; agent?: string }) => apiFetch<Mandate[]>(`/upi/mandates?${q(who)}`),
  proposals: (who: { customer?: string; agent?: string }) => apiFetch<Proposal[]>(`/upi/mandates/proposals?${q(who)}`),
  propose: (body: Record<string, unknown>) => apiFetch<Proposal>('/upi/mandates/proposals', { method: 'POST', body: JSON.stringify(body) }),
  decline: (id: string) => apiFetch<Proposal>(`/upi/mandates/proposals/${encodePathSegment(id)}/decline`, { method: 'POST', body: '{}' }),
  withdraw: (id: string) => apiFetch<Proposal>(`/upi/mandates/proposals/${encodePathSegment(id)}/withdraw`, { method: 'POST', body: '{}' }),
  revoke: (id: string, reason: string) =>
    apiFetch(`/delegations/${encodePathSegment(id)}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) }),

  payments: (who: { customer?: string; agent?: string; merchant?: string; mandate?: string }) => apiFetch<Payment[]>(`/upi/payments?${q(who)}`),
  payment: (id: string) => apiFetch<Payment>(`/upi/payments/${encodePathSegment(id)}`),
  submitPayment: (body: { token: string; merchantDid: string; merchantOrderId: string }) =>
    apiFetch<PaymentResult>('/upi/payments', { method: 'POST', body: JSON.stringify(body) }),
  mockResolve: (id: string, approve: boolean) =>
    apiFetch<Payment>(`/upi/mock-bank/payments/${encodePathSegment(id)}/resolve`, { method: 'POST', body: JSON.stringify({ approve }) }),
  replay: (decisionId: string) => apiFetch<DecisionReplay>(`/upi/decisions/${encodePathSegment(decisionId)}/replay`, { method: 'POST', body: '{}' }),
};

/** The customer consents to a proposal by signing the mandate it describes. */
export async function consentToProposal(p: Proposal, signer: IssuerSigner): Promise<Mandate> {
  const prepared = await apiFetch<Prepared>(`/upi/mandates/proposals/${encodePathSegment(p.id)}/consent/prepare`, {
    method: 'POST',
    body: JSON.stringify({ verificationMethod: signingMethod(signer, p.customerDid) }),
  });
  return apiFetch<Mandate>(`/upi/mandates/proposals/${encodePathSegment(p.id)}/consent`, {
    method: 'POST',
    body: JSON.stringify({ credentialId: prepared.credentialId, proofValue: await proofValueFor(prepared, signer) }),
  });
}

/** The customer replaces a live mandate with a narrower one. */
export async function narrowMandate(m: Mandate, constraints: Record<string, unknown>, validUntil: string | undefined, signer: IssuerSigner): Promise<Mandate> {
  const prepared = await apiFetch<Prepared>(`/upi/mandates/${encodePathSegment(m.id)}/narrow/prepare`, {
    method: 'POST',
    body: JSON.stringify({ constraints, validUntil, verificationMethod: signingMethod(signer, m.customerDid) }),
  });
  return apiFetch<Mandate>(`/upi/mandates/${encodePathSegment(m.id)}/narrow`, {
    method: 'POST',
    body: JSON.stringify({ credentialId: prepared.credentialId, proofValue: await proofValueFor(prepared, signer) }),
  });
}
